import { Icon } from 'components/Icon';
import { render, type JSXChild } from 'engine/index';
import { ArrowLeftRight, Book, CircleHelp, Dices, DoorClosed, Frown, Hourglass, Stars, ThumbsUp, X } from 'lucide-static';
import { unreachable } from 'shared/util';
import { rpc, State } from 'engine/rpc';
import {
	teamKleurenPlayerColors,
	type BoardPanelColor,
	type PersonalityColor,
	type Profile,
	type TeamKleurenCard,
	type TeamKleurenUpdate,
} from 'shared/modelTypes/team-kleuren';
import { signOut } from 'components/Login';
import { setUserIconColor, showModal, StatBubble } from 'components/AppShell';
import { Game } from './game';
import { SelectorButton } from 'components/SelectorButton';
import { Timer } from 'components/Timer';

export class TeamKleuren extends Game<'team-kleuren-spel'> {
	override maxPlayers: number = 6;

	shellGoodCardEl!: HTMLElement;
	shellBadCardEl!: HTMLElement;
	shellScoreEl!: HTMLElement;

	override getGameName(): string {
		return __`teamKleuren.gameTitle`;
	}

	override defineBottomUI(): JSXChild {
		return (
			<>
				<StatBubble
					onClick={() => showModal(<CardList label={__`teamKleuren.goodCards`} cards={State.goodCards} />)}
					color="yellow"
					label={__`teamKleuren.goodCards`}
					value="0"
					id="good-card-count"
				/>
				<StatBubble
					onClick={() => showModal(<CardList label={__`teamKleuren.badCards`} cards={State.badCards} />)}
					color="red"
					label={__`teamKleuren.badCards`}
					value="0"
					id="bad-card-count"
				/>
				<StatBubble color="green" label="Score" value="0" id="score" />
			</>
		);
	}

	override onShellCreated(view: HTMLElement): void {
		super.onShellCreated(view);

		this.shellGoodCardEl = view.querySelector('#good-card-count')!;
		this.shellBadCardEl = view.querySelector('#bad-card-count')!;
		this.shellScoreEl = view.querySelector('#score')!;
	}

	override handleUpdate(update: TeamKleurenUpdate): void {
		const sessionId = State.gameId.toString();

		const playerId = State.playerId;

		switch (update.kind) {
			case 'show-login-screen':
				this.showLoginScreen();
				break;
			case 'show-register-screen':
				this.showRegisterScreen('player', teamKleurenPlayerColors);
				break;
			case 'show-turnstart-screen':
				render(
					this.shellContentEl,
					<ChooseLandingSpot
						onChosen={(color) =>
							rpc.sendTeamKleurenMessage.query({
								sessionId,
								update: { kind: 'choose-landing-spot', playerId, panelColor: color },
							})
						}
					/>,
				);
				break;
			case 'show-choosecard-screen':
				{
					const cb = (card: string) => {
						if (!card || (update.swapOptions?.length ?? 0) < 4) {
							rpc.sendTeamKleurenMessage.query({ sessionId, update: { kind: 'choose-card', playerId, card } });
							return;
						}

						showModal(
							(confirm) => (
								<ChooseSwapCard
									options={update.swapOptions!}
									onChosen={(swapCard) => {
										if (!swapCard) {
											// keep current hand
											rpc.sendTeamKleurenMessage.query({
												sessionId,
												update: { kind: 'choose-card', playerId, card: '' },
											});
										} else {
											// pick up new card and swap out selected
											rpc.sendTeamKleurenMessage.query({
												sessionId,
												update: { kind: 'choose-card', playerId, card, swapFor: swapCard },
											});
										}
										confirm();
									}}
								/>
							),
							{ passiveDismiss: false },
						);
					};

					render(
						this.shellContentEl,
						<ChooseCard
							onBoardPanel={update.forColor}
							options={update.options}
							playerName={update.activePlayerName}
							onChosen={update.activePlayerName ? undefined : cb}
						/>,
					);
				}
				break;
			case 'show-answer-screen':
				render(
					this.shellContentEl,
					<DisplayAnswer
						answer={_k(update.answer)}
						selectedAnswer={update.selectedAnswer ? _k(update.selectedAnswer) : undefined}
					/>,
				);
				// if (State.lastAnswerCorrect) {
				// 	State.score += 1;
				// 	shellScoreEl.innerText = State.score.toString();
				// }

				break;
			case 'show-answer-screen-colors':
				render(this.shellContentEl, <DisplayAnswerColors answer1={update.answer1} answer2={update.answer2} />);
				break;

			case 'show-colorquiz-screen':
				render(
					this.shellContentEl,
					<ColorQuiz
						correctAnswer={update.correctAnswer}
						question={update.question}
						timerFrom={update.timerFrom}
						timerTo={update.timerTo}
						onAnswer={(color) =>
							rpc.sendTeamKleurenMessage.query({
								sessionId,
								update: { kind: 'choose-colorquiz-answer', playerId, color },
							})
						}
					/>,
				);
				break;
			case 'show-textquiz-screen':
				render(
					this.shellContentEl,
					<TextQuiz
						correctAnswer={update.correctAnswer}
						question={update.question}
						answers={update.answers}
						timerFrom={update.timerFrom}
						timerTo={update.timerTo}
						onAnswer={(answer) =>
							rpc.sendTeamKleurenMessage.query({
								sessionId,
								update: { kind: 'choose-textquiz-answer', playerId, answer },
							})
						}
					/>,
				);
				break;
			case 'show-randomevent-screen':
				render(
					this.shellContentEl,
					<RandomEvent
						action={update.action}
						playerPicker={update.playerPicker}
						players={update.players}
						onChoose={(player) =>
							rpc.sendTeamKleurenMessage.query({
								sessionId,
								update: { kind: 'choose-randomevent-target', playerId, targetPlayerColor: player?.color ?? '' },
							})
						}
					/>,
				);
				break;
			case 'show-steal-screen':
				render(
					this.shellContentEl,
					<StealCard
						players={update.players}
						onAnswer={(playerColor, card, isGoodCard) => {
							const shouldSwap = isGoodCard ? update.goodCards.length >= 4 : update.badCards.length >= 4;
							if (!shouldSwap) {
								rpc.sendTeamKleurenMessage.query({
									sessionId,
									update: {
										kind: 'choose-steal-target',
										playerId,
										targetPlayerColor: playerColor,
										targetPlayerCard: card,
										isGoodCard,
									},
								});
							} else {
								showModal(
									(confirm) => (
										<ChooseSwapCard
											options={isGoodCard ? update.goodCards : update.badCards}
											onChosen={(swapCard) => {
												rpc.sendTeamKleurenMessage.query({
													sessionId,
													update: {
														kind: 'choose-steal-target',
														playerId,
														targetPlayerColor: playerColor,
														targetPlayerCard: card,
														isGoodCard,
														swapFor: swapCard,
													},
												});
												confirm();
											}}
										/>
									),
									{ passiveDismiss: false },
								);
							}
						}}
					/>,
				);
				break;
			case 'show-wait-screen':
				render(this.shellContentEl, <WaitForTurn playerName={update.playerName} />);
				break;
			case 'show-endgamerounds-screen':
				render(this.shellContentEl, <EndGameRounds />);
				break;
			case 'show-profilequiz-screen':
				render(
					this.shellContentEl,
					<ProfileQuiz
						correctAnswer={[update.correctAnswer1, update.correctAnswer2]}
						question={update.question}
						goodCards={update.goodCards}
						badCards={update.badCards}
						onAnswer={(color1, color2) => {
							rpc.sendTeamKleurenMessage.query({
								sessionId,
								update: { kind: 'choose-profilequiz-answer', playerId, color1, color2 },
							});
						}}
					/>,
				);
				break;
			case 'show-profile-screen':
				render(this.shellContentEl, <ProfileSummary profile={update.profile} reportUrl={update.reportUrl} />);
				break;
			case 'update-players-state':
				this.updatePlayersState(update);
				break;
				break;
			case 'update-player-state':
				State.name = update.player.name;
				State.color = update.player.color;
				State.goodCards = update.player.goodCards;
				State.badCards = update.player.badCards;
				State.score = update.player.score;

				this.shellNameEl.innerText = State.name;
				this.shellGoodCardEl.innerText = State.goodCards.length.toString();
				this.shellBadCardEl.innerText = State.badCards.length.toString();
				this.shellScoreEl.innerText = State.score.toString();
				setUserIconColor(this.userIconEl, State.color);

				break;
			case 'trigger-effect':
				this.triggerEffect(update);
				break;
			default:
				unreachable(update);
		}
	}
}

export function GetColorText(color: string) {
	switch (color) {
		case 'green':
			return __`teamKleuren.personalityColor.green`;
		case 'red':
			return __`teamKleuren.personalityColor.red`;
		case 'blue':
			return __`teamKleuren.personalityColor.blue`;
		case 'yellow':
			return __`teamKleuren.personalityColor.yellow`;
	}
}

// step 4 (roll dice and choose landing spot color)
export function ChooseLandingSpot({ onChosen }: { onChosen: (chosen: BoardPanelColor) => void }) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-8 p-6 md:max-w-screen-md">
			<div class="text-center text-xl text-gray-800">{__`teamKleuren.chooseLandingSpot`}</div>
			<div class="grid grid-cols-2 gap-4 md:grid-cols-3">
				<BoardPanel onSelect={onChosen} kind="yellow" />
				<BoardPanel onSelect={onChosen} kind="red" />
				<BoardPanel onSelect={onChosen} kind="green" />
				<BoardPanel onSelect={onChosen} kind="blue" />
				<BoardPanel onSelect={onChosen} kind="purple" />
				<BoardPanel onSelect={onChosen} kind="cyan" />
			</div>
		</div>
	);
}

// step 4 (choose card - red/yellow)
export function ChooseCard({
	onBoardPanel,
	options,
	onChosen,
	playerName,
}: {
	onBoardPanel: 'red' | 'yellow';
	options: { name: string; taken: boolean }[];
	onChosen?: (card: string) => void;
	playerName?: string;
}) {
	return (
		<div class="flex h-fit w-full flex-col items-center gap-4 p-6 md:max-w-screen-md">
			<BoardPanel small kind={onBoardPanel} />
			<div class="text-center text-xl text-gray-800">
				{playerName ? __`teamKleuren.playerTurn:${playerName}` : __`teamKleuren.yourTurn`}
				<br />
				{onBoardPanel == 'red' && __`teamKleuren.onBadDay`}
				{onBoardPanel == 'yellow' && __`teamKleuren.onGoodDay`}
			</div>
			<div class="grid grid-cols-2 gap-4 md:grid-cols-3" style={playerName ? 'opacity: 0.6; pointer-events: none' : ''}>
				{options.map((option) => (
					<Card taken={option.taken} label={option.name} onTap={onChosen ? () => onChosen(option.name) : undefined}></Card>
				))}
			</div>
			{onChosen && (
				<button class="rounded-md bg-gray-200 p-4 text-gray-800" on:click={onChosen ? () => onChosen('') : undefined}>
					{__`teamKleuren.skipTurn`}
				</button>
			)}
		</div>
	);
}

// step 4 (color quiz - green)
export function ColorQuiz({
	question,
	correctAnswer,
	timerFrom,
	timerTo,
	onAnswer,
}: {
	question: string;
	correctAnswer: PersonalityColor;
	timerFrom: number;
	timerTo: number;
	onAnswer: (color: PersonalityColor) => void;
}) {
	const view: HTMLElement = (
		<div class="flex h-fit w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<BoardPanel kind={'green'} />
			<div class="text-center text-xl text-gray-800">
				<p>{__`teamKleuren.colorQuiz.questionIntro`}</p>
				<p class="font-bold">{question}</p>
				<p>{__`teamKleuren.colorQuiz.preference`}</p>
			</div>
			<Timer timerFrom={timerFrom} timerTo={timerTo} />
			<ColorSelector
				onSelect={(col) => {
					if (col === correctAnswer) {
						State.lastAnswerCorrect = true;
					} else {
						State.lastAnswerCorrect = false;
					}
					onAnswer(col);
				}}
			/>
			<span answer-feedback style="opacity:0">
				k
			</span>
		</div>
	);
	return view;
}

// step 4 (TextQuiz - blue)
export function TextQuiz({
	question,
	correctAnswer,
	answers,
	timerFrom,
	timerTo,
	onAnswer,
}: {
	question: string;
	answers: [string, string, string, string];
	correctAnswer: number;
	timerFrom: number;
	timerTo: number;
	onAnswer: (answer: number) => void;
}) {
	const view: HTMLElement = (
		<div class="flex h-fit w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<BoardPanel kind={'blue'} />
			<div class="text-center text-xl text-gray-800">
				<p>{__`teamKleuren.textQuiz.questionIntro`}</p>
				<p class="font-bold">{question}</p>
				<p>{__`teamKleuren.textQuiz.responseIntro`}</p>
			</div>
			<Timer timerFrom={timerFrom} timerTo={timerTo} />
			<AnswerSelector
				answers={answers}
				onSelect={(i) => {
					if (i === correctAnswer) {
						State.lastAnswerCorrect = true;
					} else {
						State.lastAnswerCorrect = false;
					}
					onAnswer(i);
				}}
			/>
			<span answer-feedback>{__`teamKleuren.textQuiz.feedback`}</span>
		</div>
	);
	return view;
}

// step 4 (Random event - purple)

export function RandomEvent({
	action,
	playerPicker,
	players,
	onChoose,
}: {
	action: string;
	playerPicker?: boolean;
	players: { name: string; color: string }[];
	onChoose: (d: { name: string; color: string } | null) => void;
}) {
	const view: HTMLElement = (
		<div class="flex w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<BoardPanel kind={'purple'} />
			<div class="text-center text-xl text-gray-800">
				{__`teamKleuren.randomEvent.intro`}
				<p class="font-bold">{action}</p>
			</div>
			{playerPicker && (
				<PlayerSelector
					players={players}
					onSelect={(p) => {
						onChoose(p);
					}}
				/>
			)}
			{!playerPicker && (
				<button class="rounded-md bg-theme-600 p-4 text-theme-50" on:click={() => onChoose(null)}>
					{__`teamKleuren.randomEvent.continue`}
				</button>
			)}
		</div>
	);
	return view;
}

type StealPlayer = { name: string; color: string; goodCards: string[]; badCards: string[] };
export function StealCard({
	players,
	onAnswer,
}: {
	players: StealPlayer[];
	onAnswer: (playerColor: string, card: string, isGoodCard: boolean) => void;
}) {
	const showStealCard = (player: StealPlayer) => {
		stepText.innerText = __`teamKleuren.stealCard.selectCard:${player.name}`;
		render(
			stepContainer,
			<>
				<strong>Op een goede dag:</strong>
				<div class="mb-3 grid grid-cols-2 gap-3">
					{player.goodCards.map((c) => (
						<Card label={c} onTap={() => onAnswer(player.color, c, true)} />
					))}
				</div>
				<strong>Op een slechte dag:</strong>
				<div class="grid grid-cols-2 gap-3">
					{player.badCards.map((c) => (
						<Card label={c} onTap={() => onAnswer(player.color, c, false)} />
					))}
				</div>
			</>,
		);
	};

	const view: HTMLElement = (
		<div class="flex h-fit w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<BoardPanel kind={'cyan'} />
			<div class="text-center text-xl text-gray-800">
				<p>{__`teamKleuren.stealCard.intro`}</p>
				<p step-text>{__`teamKleuren.stealCard.selectPlayer`}</p>
			</div>
			<div step-container class="mt-4 flex flex-col gap-2">
				{players.map((p) => (
					<SelectorButton on:click={() => showStealCard(p)}>{p.name}</SelectorButton>
				))}
			</div>
		</div>
	);

	const stepContainer = view.querySelector('[step-container]') as HTMLElement;
	const stepText = view.querySelector('[step-text]') as HTMLElement;
	return view;
}

// step 4 (not your turn)
export function WaitForTurn({ playerName }: { playerName: string }) {
	return (
		<div class="flex flex-col items-center justify-center gap-2 p-6 md:max-w-screen-md">
			<Icon icon={Hourglass} class="size-20 text-gray-300" />
			<div class="text-center text-xl text-gray-800">
				<span class="font-bold text-gray-900">{playerName}</span> {__`teamKleuren.waitForTurn.action`}
			</div>
		</div>
	);
}

export function DisplayAnswer({ answer, selectedAnswer }: { answer: string; selectedAnswer: string | undefined }) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-2 p-6 md:max-w-screen-md">
			<Icon icon={Book} class="size-20 text-gray-300" />
			<div class="text-center text-xl text-gray-800">
				{selectedAnswer && (
					<>
						{__`teamKleuren.displayAnswer.selectedAnswer`}
						<br /> <strong>{selectedAnswer}</strong>
						<br />
					</>
				)}
				{__`teamKleuren.displayAnswer.correctAnswer`}
				<br /> <strong>{answer}</strong>
			</div>
		</div>
	);
}

export function DisplayAnswerColors({ answer1, answer2 }: { answer1: string; answer2: string }) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-2 p-6 md:max-w-screen-md">
			<Icon icon={Book} class="size-20 text-gray-300" />
			<div class="text-center text-xl text-gray-800">
				{__`teamKleuren.displayAnswer.correctAnswer`}
				<br />
				<strong>
					{GetColorText(answer1)}
					{answer2 ? ', ' + GetColorText(answer2) : ''}
				</strong>
			</div>
		</div>
	);
}

export function EndGameRounds() {
	return (
		<div class="flex w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<Icon icon={Stars} class="size-20 text-yellow-500" />
			<h2 class="text-xl font-bold text-gray-900">{__`teamKleuren.endGame.title`}</h2>
			<p class="text-gray-700">{__`teamKleuren.endGame.description`}</p>
			<small class="mt-5 text-gray-600">{__`teamKleuren.endGame.waitLeader`}</small>
		</div>
	);
}

export function ProfileQuiz({
	question,
	correctAnswer,
	goodCards,
	badCards,
	onAnswer,
}: {
	question: string;
	correctAnswer: [PersonalityColor, PersonalityColor];
	goodCards: string[];
	badCards: string[];
	onAnswer: (color1: PersonalityColor, color2: PersonalityColor) => void;
}) {
	const values: [PersonalityColor, PersonalityColor] = ['' as PersonalityColor, '' as PersonalityColor];

	const selected = (value: PersonalityColor, index: number) => {
		values[index] = value;
		if (correctAnswer.includes(values[0]) && correctAnswer.includes(values[1])) {
			State.lastAnswerCorrect = true;
		} else {
			State.lastAnswerCorrect = false;
		}

		// only submit if you picked both
		if (values[0] && values[1]) {
			onAnswer(values[0], values[1]);
		}
	};

	const view: HTMLElement = (
		<div class="flex w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<div class="text-center text-xl text-gray-800">
				<p class="font-bold">{question}</p>
				<p>
					<strong>{__`teamKleuren.profileQuiz.goodDay`}: </strong> {goodCards.join(', ') || __`teamKleuren.profileQuiz.noCards`}
				</p>
				<p>
					<strong>{__`teamKleuren.profileQuiz.badDay`}: </strong> {badCards.join(', ') || __`teamKleuren.profileQuiz.noCards`}
				</p>
			</div>
			<h3>{__`teamKleuren.profileQuiz.primaryColor`}</h3>
			<ColorSelector
				onSelect={(col) => {
					selected(col, 0);
				}}
			/>
			<h3>{__`teamKleuren.profileQuiz.secondaryColor`}</h3>
			<ColorSelector
				onSelect={(col) => {
					selected(col, 1);
				}}
			/>

			<span answer-feedback style="opacity:0">
				k
			</span>
		</div>
	);
	return view;
}

export function ProfileSummary({ profile, reportUrl }: { profile: Profile; reportUrl: string }) {
	return (
		<div class="flex h-fit w-full flex-col gap-2 p-6 md:max-w-screen-md">
			<h3 class="text-xl font-bold">{__`teamKleuren.profileSummary.yourProfile`}</h3>
			<p>
				{__`teamKleuren.profileSummary.name`}: {profile.name}
			</p>
			<p>
				{__`teamKleuren.profileSummary.personality`}
				{GetColorText(profile.personality1)}
				{profile.personality2 ? `, ${GetColorText(profile.personality2)}` : ''}
			</p>
			<h2 class="text-lg font-bold">{__`teamKleuren.profileSummary.description`}</h2>
			<p class="font-bold">
				{GetColorText(profile.personality1)} ({profile.details1?.trait})
			</p>
			<pre class="w-full whitespace-normal font-sans" __innerHTML={profile.details1?.details}></pre>
			{profile.details2 ? (
				<>
					<p class="font-bold">
						{GetColorText(profile.personality2)} ({profile.details2.trait})
					</p>
					<pre class="w-full whitespace-normal font-sans" __innerHTML={profile.details2.details}></pre>
				</>
			) : (
				''
			)}

			<h2 class="text-lg font-bold">{__`teamKleuren.profileSummary.chosenGoodCards`}</h2>
			<div class="flex shrink-0 gap-3 overflow-x-auto">
				{profile.goodCards.map((c) => (
					<Card label={c.name} color={c.color} />
				))}
			</div>
			<h2 class="text-lg font-bold">{__`teamKleuren.profileSummary.chosenBadCards`}</h2>
			<div class="flex shrink-0 gap-3 overflow-x-auto">
				{profile.badCards.map((c) => (
					<Card label={c.name} color={c.color} />
				))}
			</div>

			<div class="flex gap-3">
				{reportUrl && (
					<button
						class="rounded-md bg-theme-600 p-4 text-theme-50"
						on:click={() => {
							const path = reportUrl;
							const url = new URL(path, window.location.origin);
							const link = document.createElement('a');
							link.href = url.toString();
							link.download = path.split('/').pop() || 'download';
							document.body.appendChild(link);
							link.click();
							document.body.removeChild(link);
						}}
					>
						{__`teamKleuren.profileSummary.downloadReport`}
					</button>
				)}
				<button class="rounded-md bg-gray-200 p-4 text-gray-900" on:click={signOut}>
					{__`teamKleuren.profileSummary.endGame`}
				</button>
			</div>
		</div>
	);
}

function ColorPane({ color }: { color: PersonalityColor }) {
	const colorClasses = (() => {
		switch (color) {
			case 'red':
				return 'bg-red-600 text-red-50 border-red-700';
			case 'green':
				return 'bg-green-600 text-green-50 border-green-700';
			case 'blue':
				return 'bg-blue-600 text-blue-50 border-blue-700';
			case 'yellow':
				return 'bg-yellow-400 text-yellow-900 border-yellow-500';
		}
	})();
	return (
		<div
			color={color}
			class={
				'size-16 cursor-pointer rounded-md border transition-transform hover:scale-110 hover:shadow-sm data-[selected]:scale-105 data-[selected]:ring data-[selected]:ring-gray-800 data-[selected]:ring-offset-2 data-[selected]:ring-offset-gray-100 md:size-20 ' +
				colorClasses
			}
		></div>
	);
}
export function ColorSelector({ onSelect }: { onSelect: (color: PersonalityColor) => void }) {
	const view = (
		<div
			class="grid grid-cols-2 gap-6"
			on:click={(e: MouseEvent) => {
				if (!e.target || !(e.target instanceof HTMLElement)) return;
				// Deselect others
				const children = e.target.parentElement?.children;
				if (children) {
					for (const child of children) {
						child.removeAttribute('data-selected');
					}
				}

				const colorPane = e.target.closest('[color]');
				if (colorPane) {
					onSelect(colorPane.getAttribute('color') as PersonalityColor);
					colorPane.setAttribute('data-selected', '1');
				}
			}}
		>
			<ColorPane color="green" />
			<ColorPane color="red" />
			<ColorPane color="blue" />
			<ColorPane color="yellow" />
		</div>
	);
	return view;
}

export function AnswerSelector({ answers, onSelect }: { answers: [string, string, string, string]; onSelect: (color: number) => void }) {
	const view = (
		<div
			class="flex flex-col gap-3"
			on:click={(e: MouseEvent) => {
				if (!e.target || !(e.target instanceof HTMLElement)) return;
				const colorPane = e.target.closest('[answer]');
				if (colorPane) {
					const prev = view.querySelector('[data-selected]') as HTMLElement;
					if (colorPane !== prev && prev) {
						prev.removeAttribute('data-selected');
					}

					colorPane.setAttribute('data-selected', '');
					onSelect(parseInt(colorPane.getAttribute('answer')!));
				}
			}}
		>
			{answers.map((q, i) => (
				<div
					answer={i.toString()}
					class="inline-flex cursor-pointer items-baseline gap-3 rounded-md border border-gray-200 bg-gray-100 p-3 text-gray-800 transition-transform hover:scale-105 data-[selected]:border-blue-700 data-[selected]:bg-blue-500 data-[selected]:text-blue-50"
				>
					<span class="text-xl font-bold"> {'ABCD'[i]}.</span>
					{q}
				</div>
			))}
		</div>
	);
	return view;
}

export function PlayerSelector({
	players,
	onSelect,
}: {
	players: { name: string; color: string }[];
	onSelect: (player: { name: string; color: string }) => void;
}) {
	const view = (
		<div
			class="flex flex-col gap-3"
			on:click={(e: MouseEvent) => {
				if (!e.target || !(e.target instanceof HTMLElement)) return;
				const colorPane = e.target.closest('[answer]');
				if (colorPane) {
					const p = players.find((p) => p.color == colorPane.getAttribute('color')!);
					if (p) {
						onSelect(p);
					}
				}
			}}
		>
			{players.map((q, i) => (
				<SelectorButton answer={i.toString()} color={q.color}>
					{q.name}
				</SelectorButton>
			))}
		</div>
	);
	return view;
}

function BoardPanel({ kind, small, onSelect }: { small?: boolean; kind: BoardPanelColor; onSelect?: (color: BoardPanelColor) => void }) {
	let icon: string;
	let colorClasses: string;
	switch (kind) {
		case 'yellow':
			icon = ThumbsUp;
			colorClasses = 'bg-yellow-400 text-yellow-900 border-yellow-500';
			break;
		case 'green':
			icon = CircleHelp;
			colorClasses = 'bg-green-500 text-green-50 border-green-600';
			break;
		case 'red':
			icon = Frown;
			colorClasses = 'bg-red-500 text-red-50 border-red-600';
			break;
		case 'blue':
			icon = ArrowLeftRight;
			colorClasses = 'bg-blue-500 text-blue-50 border-blue-600';
			break;
		case 'purple':
			icon = Dices;
			colorClasses = 'bg-purple-500 text-purple-50 border-purple-600';
			break;
		case 'cyan':
			icon = DoorClosed;
			colorClasses = 'bg-cyan-500 text-cyan-50 border-cyan-600';
			break;
	}

	const selectionClasses = onSelect ? 'cursor-pointer hover:scale-105 hover:opacity-95 active:scale-95 active:opacity-95 ' : '';
	return (
		<div
			data-small={small}
			class={'aspect-square rounded-sm border p-6 transition-transform data-[small]:p-2 md:p-8 ' + selectionClasses + colorClasses}
			on:click={!onSelect ? undefined : () => onSelect(kind)}
		>
			<Icon icon={icon} class="size-12 data-[small]:size-10 md:size-16" />
		</div>
	);
}

function Card({ label, color, taken, onTap }: { label: string; color?: PersonalityColor; taken?: boolean; onTap?: () => void }) {
	let colorClasses = 'border-gray-200';
	switch (color) {
		case undefined:
			break;
		case 'blue':
			colorClasses = 'border-blue-500';
			break;
		case 'red':
			colorClasses = 'border-red-500';
			break;
		case 'yellow':
			colorClasses = 'border-yellow-400';
			break;
		case 'green':
			colorClasses = 'border-green-600';
			break;
	}

	const hoverClasses = onTap
		? // ? 'cursor-pointer hover:scale-105 hover:shadow-md data-[taken]:duration-1000 data-[taken]:[transform:perspective(800px)rotate3d(0,1,0,180deg)scale(1.5)translateY(130px)] '
			'cursor-pointer hover:scale-105 hover:shadow-md '
		: '';

	return (
		<div class="h-[120px] w-[140px] shrink-0 rounded-md border border-gray-100 bg-gray-50 shadow-inner md:h-[160px] md:w-[140px]">
			<div
				data-taken={taken}
				class={
					// 'flex size-full flex-col items-center justify-between rounded-md border-2 bg-white px-6 py-4 shadow-sm transition-all data-[taken]:pointer-events-none data-[taken]:opacity-0 ' +
					'flex size-full flex-col items-center justify-between rounded-md border-2 bg-white px-6 py-4 shadow-sm transition-all data-[taken]:pointer-events-none data-[taken]:opacity-30 ' +
					hoverClasses +
					colorClasses
				}
				on:click={
					!onTap
						? undefined
						: (e: MouseEvent) => {
								(e.target as HTMLElement).setAttribute('data-taken', '');
								onTap();
							}
				}
			>
				<span class="pointer-events-none whitespace-nowrap text-sm font-semibold md:text-lg">{label}</span>
				{/* <img src="/logo.svg" class="pointer-events-none size-24 shrink-0 rotate-90 p-4" alt="bedrijfshelden logo" /> */}
				<img src="/logo.svg" class="pointer-events-none size-20 shrink-0 p-4" alt="bedrijfshelden logo" />
				{/* <span class="pointer-events-none rotate-180 whitespace-nowrap text-sm font-semibold md:text-lg">{label}</span> */}
			</div>
		</div>
	);
}

export function CardList({ label, cards }: { label: string; cards: TeamKleurenCard[] }) {
	return (
		<div class="relative flex max-h-full w-full max-w-md flex-col items-center rounded-lg border border-gray-100 bg-white p-4 shadow-lg">
			<button action-cancel class="absolute right-2 top-2 rounded-md p-2">
				<Icon icon={X} class="size-6" />
			</button>
			<h3 class="mb mb-6 text-lg font-bold text-gray-900">{_k(label)}</h3>
			<div class="grid grid-cols-2 gap-4">
				{cards.map((c) => (
					<Card label={c.name} color={c.color} />
				))}
			</div>
			{!cards.length && <small class="text-center">{__`teamKleuren.cardList.noCards`}</small>}
		</div>
	);
}

function ChooseSwapCard({ options, onChosen }: { options: string[]; onChosen?: (card: string) => void }) {
	return (
		<div class="relative flex max-h-full w-full max-w-md flex-col items-center rounded-lg border border-gray-100 bg-white p-4 shadow-lg">
			<h3 class="mb mb-6 text-lg font-bold text-gray-900">{_k('chooseSwapCard.title')}</h3>
			<div class="grid grid-cols-2 gap-4">
				{options.map((c) => (
					<Card
						onTap={() => {
							if (onChosen) onChosen(c);
							onChosen = undefined;
						}}
						label={c}
					/>
				))}
			</div>
			<button
				class="mx-auto mt-4 inline-block rounded-md bg-gray-200 p-4 text-gray-900 shadow-md"
				on:click={() => {
					if (onChosen) {
						onChosen('');
						onChosen = undefined;
					}
				}}
			>
				{_k('chooseSwapCard.passButton')}
			</button>
		</div>
	);
}
